const SHOPIFY_UTILS = (() => {
  /**
  * @returns the current shopify cart
  */
  async function getShopifyCart() {
    const shopifyResponse =await fetch(`${window.Shopify.routes.root}cart.json`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return shopifyResponse.json();
  }

  /**
  * Adds the item to the shopify cart. Throws on error; callers handle UI as needed.
  */
  async function addToShopifyCart({ variantId }) {
    if (!variantId) {
      throw new Error('No variantId provided to add to cart');
    }
    const res = await fetch(`${window.Shopify.routes.root}cart/add.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    const data = await res.json();
    if (!res.ok || data.status === 422) {
      const msg = data.description || data.message || data.errors || 'Could not add to cart';
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    if (data.errors) {
      throw new Error(typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors));
    }
    return data;
  }

  /**
  * Refreshes the cart icon bubble count via Shopify's section rendering API.
  */
  async function refreshCartIconBubble() {
    try {
      const sections = await fetch(`${window.Shopify.routes.root}?sections=cart-icon-bubble`).then((res) => res.json());
      const el = document.getElementById('cart-icon-bubble');
      if (el && sections['cart-icon-bubble']) el.innerHTML = sections['cart-icon-bubble'];
    } catch (_) { /* non-critical */ }
  }

  const DEFAULT_CART_FULL_MESSAGE = 'Your cart is full. Please remove an item before adding another.';
  const DEFAULT_STYLE_LIMIT_MESSAGE = 'You have reached the maximum number of this style allowed in your cart.';
  const DEFAULT_ADD_TO_CART_ERROR_MESSAGE = 'Failed to add item to cart. Please try again later.';

  /**
  * Thrown when adding an item would exceed one of the partner's cart limits.
  * reason is 'cart' (maxItemsPerCart) or 'style' (maxItemsPerStyle).
  */
  class CartLimitError extends Error {
    constructor(reason) {
      super(reason === 'style' ? DEFAULT_STYLE_LIMIT_MESSAGE : DEFAULT_CART_FULL_MESSAGE);
      this.name = 'CartLimitError';
      this.isCartLimit = true;
      this.reason = reason;
    }
  }

  let cartLimitsPromise = null;

  /**
  * @returns the partner's cart limits from reware-api as { maxItemsPerCart, maxItemsPerStyle }.
  * Either value is null if it is not set or could not be fetched. The result is cached for the
  * lifetime of the page.
  */
  function getCartLimits() {
    if (cartLimitsPromise) return cartLimitsPromise;
    const query = `
      query PartnerSettings($partnerUuid: String!) {
        partnerSettings(partnerUuid: $partnerUuid) {
          maxItemsPerCart
          maxItemsPerStyle
        }
      }
    `;
    const toLimit = (value) => (typeof value === 'number' ? value : null);
    cartLimitsPromise = fetchWithAppName(`${global.url}query-PartnerSettings`, {
      body: JSON.stringify({ query, variables: { partnerUuid: global.partnerUuid } }),
    })
      .then((response) => response.json())
      .then((jsonData) => {
        const partnerSettings = jsonData?.data?.partnerSettings;
        return {
          maxItemsPerCart: toLimit(partnerSettings?.maxItemsPerCart),
          maxItemsPerStyle: toLimit(partnerSettings?.maxItemsPerStyle),
        };
      })
      .catch((err) => {
        console.error('Failed to fetch partner cart limits', err);
        // Don't cache failures so the next add to cart retries.
        cartLimitsPromise = null;
        return { maxItemsPerCart: null, maxItemsPerStyle: null };
      });
    return cartLimitsPromise;
  }

  /**
  * cart.json doesn't include product metafields, so the trove-cart-data section exposes each line's
  * parent sku (style) via Shopify's section rendering API.
  *
  * @returns {Promise<{item_count: number, items: {quantity: number, parent_sku: string|null}[]}>}
  */
  async function getTroveCartData() {
    const sections = await fetch(`${window.Shopify.routes.root}?sections=trove-cart-data`).then((res) => res.json());
    const tmp = document.createElement('div');
    tmp.innerHTML = sections['trove-cart-data'];
    return JSON.parse(tmp.querySelector('#trove-cart-data').textContent);
  }

  /**
  * Checks whether one more item can be added to the cart without exceeding the partner's
  * maxItemsPerCart or maxItemsPerStyle. Limits that can't be determined (unset, failed to fetch, or
  * no parentSku provided) are skipped so customers aren't blocked from adding to cart.
  *
  * @param {object} [args]
  * @param {string|number} [args.parentSku] - The style of the item being added.
  * @returns {Promise<'cart'|'style'|null>} the limit that would be exceeded, or null if the add is ok.
  */
  async function getCartLimitReached({ parentSku } = {}) {
    const { maxItemsPerCart, maxItemsPerStyle } = await getCartLimits();
    const checkStyle = maxItemsPerStyle !== null && parentSku !== undefined && parentSku !== null && parentSku !== '';
    if (maxItemsPerCart === null && !checkStyle) return null;

    const cart = await getTroveCartData();
    if (maxItemsPerCart !== null && cart.item_count >= maxItemsPerCart) return 'cart';
    if (checkStyle) {
      const styleCount = cart.items
        .filter((item) => item.parent_sku !== null && String(item.parent_sku) === String(parentSku))
        .reduce((total, item) => total + item.quantity, 0);
      if (styleCount >= maxItemsPerStyle) return 'style';
    }
    return null;
  }

  /**
  * Adds to cart from the PDP button (or any popiless add to cart), checking the partner's cart
  * limits first and refreshing the cart bubble on success.
  *
  * @param {object} args
  * @param {string|number} args.variantId - The variant to add.
  * @param {string|number} [args.parentSku] - The style of the item, used for maxItemsPerStyle.
  * @param {HTMLElement|null} [args.messageElement] - Where to display error text. Defaults to the
  *   PDP's #add-to-cart-message, which can override the defaults via data-cart-full-message and
  *   data-style-limit-message. Pass null to skip inline messaging and handle the result yourself.
  * @returns {Promise<{success: boolean, error?: Error, limitReached?: 'cart'|'style', message?: string}>}
  */
  async function handlePDPAddToCart({
    variantId,
    parentSku,
    messageElement = document.getElementById('add-to-cart-message'),
  }) {
    if (messageElement) messageElement.innerText = '';
    try {
      const limitReached = await getCartLimitReached({ parentSku });
      if (limitReached) throw new CartLimitError(limitReached);
      await addToShopifyCart({ variantId });
      await refreshCartIconBubble();
      return { success: true };
    } catch (err) {
      let message = DEFAULT_ADD_TO_CART_ERROR_MESSAGE;
      if (err instanceof CartLimitError) {
        const customMessage = err.reason === 'style'
          ? messageElement?.dataset.styleLimitMessage
          : messageElement?.dataset.cartFullMessage;
        message = customMessage || err.message;
      }
      if (messageElement) messageElement.innerText = message;
      return { success: false, error: err, limitReached: err.reason, message };
    }
  }

  /**
  * @returns the currently selected variant id based on the options selected
  */
  function getSelectedVariant(itemUuid) {
    try {
      if (itemUuid) {
        console.log(`Using item ${itemUuid}`);
        return Object.values(variantMap)[0];
      }
      const { variantId } = window.variants.getCurrentVariant() || {};
      if (variantId) {
        return variantMap[variantId.split('gid://shopify/ProductVariant/')[1]];
      }
      throw new Error('No variant found')
    } catch (err) {
      console.log(`Failed to find variant ${err}`);
      return null;
    }
  }

  /**
  * @returns the currently selected variant id based on the options selected
  */
  function getSelectedVariantId() {
    try{
      const { variantId } = window.variants.getCurrentVariant() || {};
      if (variantId) {
        return variantId.split('gid://shopify/ProductVariant/')[1];
      }
      throw new Error('No variant found')
    } catch (err) {
      console.log(`Failed to find variant ${err}`);
      return null;
    }
  }


  return {
    addToShopifyCart,
    handlePDPAddToCart,
    getCartLimitReached,
    getCartLimits,
    refreshCartIconBubble,
    getShopifyCart,
    getSelectedVariant,
    getSelectedVariantId,
  };
})();
